// ============================================================================
// NY & NJ Regulatory Standards Mapping
// ----------------------------------------------------------------------------
// The numeric tables are NOT hand-typed. They are transcribed from the
// published regulations into src/data/standards/*.json (with the source
// snapshots and their checksums under src/data/standards/sources/) and compiled
// into src/data/regulatoryStandards.generated.ts by
// scripts/generate-standards-table.mjs.
//
// Sources of record:
//   • NYSDEC 6 NYCRR Part 375-6.8 — Soil Cleanup Objectives, Tables 375-6.8(a)
//     (Unrestricted Use) and 375-6.8(b) (Residential, Restricted-Residential,
//     Commercial, Industrial, Protection of Ecological Resources, Protection of
//     Groundwater) — 86 parameters.
//   • NJDEP Soil Remediation Standards, N.J.A.C. 7:26D Appendix 1, Tables 1-5
//     (Residential and Non-Residential Ingestion-Dermal, Residential and
//     Non-Residential Inhalation, Migration to Ground Water) — 136 parameters.
//   • A small, clearly-flagged guidance layer (src/data/standards/curation.json)
//     for analytes the soil tables do not list (bulk TPH, unspeciated chromium).
//
// All limits are mg/kg unless the limit itself says otherwise. Where a table
// publishes no numeric standard for a cell, the value is `null`.
// ============================================================================

import type { MaterialType, QualityTier } from "@/types";
import {
  GENERATED_PARAMETER_STANDARDS,
  STANDARDS_SOURCES,
} from "./regulatoryStandards.generated";
import type {
  GeneratedParameterStandard,
  PublishedLimit,
  StandardCitation,
  StandardUnit,
} from "./regulatoryStandardsTypes";

export type Jurisdiction = "NY" | "NJ";

export type { PublishedLimit, StandardCitation, StandardUnit };
export { STANDARDS_SOURCES };

export interface StandardLimit {
  /** Numeric limit in `unit`; `null` = parameter not regulated under this tier. */
  value: number | null;
  unit: StandardUnit;
}

/** A parameter row: published NY/NJ limits, aliases, citations and provenance. */
export type ParameterStandard = GeneratedParameterStandard;

// ---------------------------------------------------------------------------
// Parameter table — published, alias-aware, CAS-tagged
// ---------------------------------------------------------------------------

export const PARAMETER_STANDARDS: ParameterStandard[] = GENERATED_PARAMETER_STANDARDS;

/** Complete citation list for a parameter, including every published column. */
export function citationsForParameter(ps: ParameterStandard): StandardCitation[] {
  const out: StandardCitation[] = [];
  for (const [col, limit] of Object.entries(ps.nyLimits)) {
    out.push({
      jurisdiction: "NY",
      regulation: STANDARDS_SOURCES.ny.regulation,
      table: limit.table,
      column: STANDARDS_SOURCES.ny.columnLabels[col] ?? col,
      effectiveDate: STANDARDS_SOURCES.ny.effectiveDate,
      url: STANDARDS_SOURCES.ny.url,
    });
  }
  for (const [col, limit] of Object.entries(ps.njLimits)) {
    out.push({
      jurisdiction: "NJ",
      regulation: STANDARDS_SOURCES.nj.regulation,
      table: limit.table,
      column: STANDARDS_SOURCES.nj.columnLabels[col] ?? col,
      effectiveDate: STANDARDS_SOURCES.nj.effectiveDate,
      url: STANDARDS_SOURCES.nj.tables?.[col]?.url ?? STANDARDS_SOURCES.nj.url,
    });
  }
  return [...out, ...ps.extraCitations];
}
// ---------------------------------------------------------------------------
// Material applicability — which parameter families the regulator requires
// for each material type before it can be reused on a NY/NJ site.
// ---------------------------------------------------------------------------

export type ParamFamily = ParameterStandard["family"];

interface MaterialApplicability {
  /** Standards that may legally cover this material at all. */
  applies: { ny: boolean; nj: boolean };
  /** Required analyte families for a typical Phase II / SCO comparison. */
  required: ParamFamily[];
  /** Conditional (only if site history suggests). */
  conditional: ParamFamily[];
  /** Free-form regulator note rendered in the UI. */
  note: string;
}

export const MATERIAL_APPLICABILITY: Record<MaterialType, MaterialApplicability> = {
  "topsoil": {
    applies: { ny: true, nj: true },
    required: ["metal", "pah", "voc"],
    conditional: ["pcb", "pesticide", "petroleum"],
    note: "Imported soil/fill must meet NY Part 375 SCOs and NJ RDCSRS at the receiving-site use category. Topsoil for residential reuse must additionally meet NJ residential direct contact criteria.",
  },
  "clean-fill": {
    applies: { ny: true, nj: true },
    required: ["metal", "pah", "voc", "svoc"],
    conditional: ["pcb", "pesticide", "petroleum"],
    note: "Material marketed as clean fill must meet NY Part 375 Unrestricted SCOs and NJ RDCSRS Residential standards on every regulated parameter.",
  },
  "general-fill": {
    applies: { ny: true, nj: true },
    required: ["metal", "pah", "voc"],
    conditional: ["pcb", "pesticide", "petroleum"],
    note: "Use category controlled by NY Part 375 (Restricted Residential / Commercial / Industrial) and NJ NRDCSRS depending on the receiving-site land use.",
  },
  "contaminated-soil": {
    applies: { ny: true, nj: true },
    required: ["metal", "pah", "voc", "svoc", "petroleum"],
    conditional: ["pcb", "pesticide"],
    note: "Must be characterized against NY Part 375 SCOs and NJ direct-contact standards; receiving facility must hold an applicable solid-waste / processing permit.",
  },
  "recycled-concrete": {
    applies: { ny: true, nj: true },
    required: ["metal"],
    conditional: ["pah", "petroleum"],
    note: "NJDEP RCA Guidance (2013) and NYSDEC DER-10 §5.4(e) — recognizable, uncontaminated concrete is exempt from solid-waste classification but must be free of regulated coatings, sealants, and adhered soil exceeding NY UU SCOs.",
  },
  "aggregates": {
    applies: { ny: true, nj: true },
    required: ["metal"],
    conditional: ["pah", "petroleum"],
    note: "Virgin aggregate is unregulated; processed/recycled aggregate must meet NY UU SCOs and NJ RDCSRS for any adhered fines.",
  },
  "stone": {
    applies: { ny: false, nj: false },
    required: [],
    conditional: ["metal"],
    note: "Naturally occurring stone is not subject to NY Part 375 or NJ SRS unless processed with regulated additives.",
  },
  "sand": {
    applies: { ny: true, nj: true },
    required: ["metal"],
    conditional: ["pah", "petroleum"],
    note: "Manufactured / dredged sand requires NY UU SCO and NJ RDCSRS verification; virgin natural sand is generally unregulated.",
  },
  "gravel": {
    applies: { ny: false, nj: false },
    required: [],
    conditional: ["metal"],
    note: "Virgin gravel is unregulated; recycled gravel follows the recycled-concrete pathway.",
  },
  "crushed-stone": {
    applies: { ny: false, nj: false },
    required: [],
    conditional: ["metal"],
    note: "Virgin crushed stone is unregulated under Part 375 and NJ SRS.",
  },
  "mulch": {
    applies: { ny: false, nj: false },
    required: [],
    conditional: ["metal", "pesticide"],
    note: "Wood mulch is regulated under NYSDEC Part 360 (registration) rather than soil cleanup objectives; pesticide screening recommended.",
  },
  "other": {
    applies: { ny: true, nj: true },
    required: ["metal", "pah", "voc"],
    conditional: ["pcb", "pesticide", "petroleum", "svoc"],
    note: "Default screening applies — confirm receiving-site use category before reuse.",
  },
};

// ---------------------------------------------------------------------------
// Tier mapping
// ---------------------------------------------------------------------------

/** Maps the app's QualityTier onto each regulator's published tier name. */
export function nyTierKey(tier: QualityTier): keyof ParameterStandard["ny"] {
  switch (tier) {
    case "unrestricted":
      return "unrestricted";
    case "residential":
      return "residential"; // Restricted Residential
    case "commercial":
      return "commercial";
    case "industrial":
    case "restricted":
      return "industrial";
  }
}

export function njTierKey(tier: QualityTier): keyof ParameterStandard["nj"] {
  switch (tier) {
    case "unrestricted":
    case "residential":
      return "residential";
    case "commercial":
    case "industrial":
    case "restricted":
      return "nonresidential";
  }
}

// ---------------------------------------------------------------------------
// Parameter resolution (canonical, alias-aware, CAS-aware)
// ---------------------------------------------------------------------------

const NORMALIZE_RE = /[\s\-_(),'.]/g;
function norm(s: string): string {
  return s.toLowerCase().replace(NORMALIZE_RE, "");
}

const RESOLVE_CACHE = new Map<string, ParameterStandard | null>();

export function resolveParameter(input: string, cas?: string): ParameterStandard | null {
  const cacheKey = `${cas ?? ""}::${input}`;
  if (RESOLVE_CACHE.has(cacheKey)) return RESOLVE_CACHE.get(cacheKey)!;
  if (cas) {
    const byCas = PARAMETER_STANDARDS.find((p) => p.cas === cas) ?? null;
    if (byCas) {
      RESOLVE_CACHE.set(cacheKey, byCas);
      return byCas;
    }
  }
  const target = norm(input);
  const hit =
    PARAMETER_STANDARDS.find((p) => norm(p.name) === target) ??
    PARAMETER_STANDARDS.find((p) => p.aliases.some((a) => norm(a) === target)) ??
    null;
  RESOLVE_CACHE.set(cacheKey, hit);
  return hit;
}

// ---------------------------------------------------------------------------
// Limit lookup — material- + tier-aware
// ---------------------------------------------------------------------------

export interface LimitLookup {
  jurisdiction: Jurisdiction;
  parameter: string;
  /** Numeric limit in `unit`; null if unregulated for this material/tier combination. */
  limit: number | null;
  unit: StandardUnit;
  /** Human-readable standard name applied. */
  standard: string;
  /** True if the material is even covered by this jurisdiction. */
  applicable: boolean;
}

export function getLimit(
  parameter: string,
  material: MaterialType,
  tier: QualityTier,
  jurisdiction: Jurisdiction,
  cas?: string,
): LimitLookup {
  const ps = resolveParameter(parameter, cas);
  const applicability = MATERIAL_APPLICABILITY[material];
  const applicable = jurisdiction === "NY" ? applicability.applies.ny : applicability.applies.nj;
  if (!ps) {
    return {
      jurisdiction, parameter, limit: null, unit: "mg/kg",
      standard: jurisdiction === "NY" ? "NYSDEC Part 375" : "NJDEP SRS",
      applicable,
    };
  }
  if (!applicable) {
    return {
      jurisdiction, parameter: ps.name, limit: null, unit: ps.unit,
      standard: jurisdiction === "NY" ? "NYSDEC Part 375 (not applicable)" : "NJDEP SRS (not applicable)",
      applicable: false,
    };
  }
  if (jurisdiction === "NY") {
    const key = nyTierKey(tier);
    const value = ps.ny[key] ?? null;
    return {
      jurisdiction, parameter: ps.name, limit: value, unit: ps.unit,
      standard: `NYSDEC Part 375-6.8 (${nyTierLabel(tier)})`,
      applicable: true,
    };
  }
  const key = njTierKey(tier);
  const value = ps.nj[key] ?? null;
  return {
    jurisdiction, parameter: ps.name, limit: value, unit: ps.unit,
    standard: `NJDEP ${tier === "unrestricted" || tier === "residential" ? "RDCSRS" : "NRDCSRS"}`,
    applicable: true,
  };
}

function nyTierLabel(tier: QualityTier): string {
  switch (tier) {
    case "unrestricted": return "Unrestricted Use";
    case "residential": return "Restricted Residential";
    case "commercial": return "Commercial";
    case "industrial":
    case "restricted":
      return "Industrial";
  }
}

// ---------------------------------------------------------------------------
// Backwards-compatible exports — keep the original shape so existing callers
// (complianceAudit, regulatoryFit, Analysis page) keep compiling without
// behavioural regressions.
// ---------------------------------------------------------------------------

/** Null means New York publishes no objective for that use — never coerce to 0. */
const legacyTier = (ps: ParameterStandard, tier: keyof ParameterStandard["ny"]): number | null =>
  ps.ny[tier] ?? null;

export const nyPart375SCOs = {
  name: "NYSDEC Part 375 Soil Cleanup Objectives",
  state: "NY" as const,
  parameters: PARAMETER_STANDARDS.filter((p) =>
    (["unrestricted", "residential", "commercial", "industrial"] as const).some((t) => p.ny[t] != null),
  ).map((p) => ({
    name: p.name,
    unrestricted: legacyTier(p, "unrestricted"),
    residential: legacyTier(p, "residential"),
    commercial: legacyTier(p, "commercial"),
    industrial: legacyTier(p, "industrial"),
    unit: p.unit,
    screening: p.basis === "guidance",
  })),
};

export const njRDCSRS = {
  name: "NJDEP Residential Direct Contact Soil Remediation Standards",
  state: "NJ" as const,
  parameters: PARAMETER_STANDARDS.filter((p) => p.nj.residential != null).map((p) => ({
    name: p.name,
    unrestricted: p.nj.residential ?? 0,
    residential: p.nj.residential ?? 0,
    commercial: p.nj.nonresidential ?? p.nj.residential ?? 0,
    industrial: p.nj.nonresidential ?? p.nj.residential ?? 0,
    unit: p.unit,
  })),
};
